// src/pages/Patientsappointment.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Container, Button, Card, Alert } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import {
  getPatientById,
  getAccountByPatientId,
} from "../api";

import { useEditPatientModal } from "../Components/PatientsAccounts/EditPatients";
import CheckEligibility from "../Components/PatientsAccounts/CheckEligibility";
import Session from "../Components/Sessions/Session";
import CaseManagerPanel from "../Components/Cases/CaseModal";

export function Patientsappointment() {
  const { id } = useParams();

  const [patient, setPatient] = useState(null);
  const [account, setAccount] = useState({});
  const [eligibilityData, setEligibilityData] = useState(null);
  const [activeSection, setActiveSection] = useState("info"); // "info" | "eligibility" | "cases"

  // collapse/expand the entire Patient Info card
  const [showPatientInfo, setShowPatientInfo] = useState(true);

  const [isMobile, setIsMobile] = useState(false);
  const [userRole, setUserRole] = useState(false);

  // cases mobile picker toggle
  const [showCasesPicker, setShowCasesPicker] = useState(false);

  // pass referrer source later if you have it
  const existingReferrers = useMemo(() => [], []);
  const { open: openEditPatient, Modal: EditPatientModalHost } =
    useEditPatientModal();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("user");
    if (token) {
      const decoded = jwtDecode(token);
      const roles = Array.isArray(decoded?.role) ? decoded.role : [];
      setUserRole(
        roles.includes("Practitioner") || roles.includes("SuperAdmin")
      );
    }
  }, []);

  useEffect(() => {
    async function load() {
      try {
        const p = await getPatientById(id);
        setPatient(p);
        if (p?._id) {
          const acc = await getAccountByPatientId(p._id);
          setAccount(acc);
        }
      } catch (e) {
        console.error("Error fetching patient/account:", e);
      }
    }
    if (id) load();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16).replace("T", " ");
  };

  const PatientInfoCard = () => {
    if (!patient) return <p>Loading patient information...</p>;
    const getStatusClass = (status) =>
      (status || "").toLowerCase().includes("active")
        ? "text-success"
        : "text-danger";

    return (
      <Card className="border-0">
        <Card.Body>
          <Row className="mb-2 align-items-center">
            <Col>
              <strong>
                {patient?.patientTitle} {patient?.patientFullNames}{" "}
                {patient?.patientLastName}
              </strong>
            </Col>
            <Col className="text-end">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowPatientInfo((s) => !s)}
              >
                {showPatientInfo ? (
                  <>
                    <i className="fa-solid fa-eye-slash"></i> Hide Patient
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-eye"></i> Show Patient
                  </>
                )}
              </Button>
            </Col>
          </Row>

          {showPatientInfo && (
            <>
              <Row>
                <Col xs={12} md={4} className="mb-3 mb-md-0">
                  {patient?.patientNumber && (
                    <>
                      <i className="fa-regular fa-hashtag me-2 text-primary"></i>
                      {patient.patientNumber} <br />
                    </>
                  )}
                  {patient?.patientIdNumber && (
                    <>
                      <i className="fa-solid fa-id-card me-1 text-primary"></i>
                      {patient.patientIdNumber} <br />
                    </>
                  )}
                  {patient?.patientCellNumber && (
                    <>
                      <i className="fas fa-phone-alt me-2 text-primary"></i>
                      {patient.patientCellNumber} <br />
                    </>
                  )}
                  {patient?.patientEmail && (
                    <>
                      <i className="fas fa-envelope me-2 text-primary"></i>
                      {patient.patientEmail} <br />
                    </>
                  )}
                  {patient?.patientGender && (
                    <>
                      <i className="fa-solid fa-venus-mars me-2 text-primary"></i>
                      {patient.patientGender === "1"
                        ? "Male"
                        : patient.patientGender === "2"
                        ? "Female"
                        : "Unspecified"}{" "}
                      <br />
                    </>
                  )}
                  {patient?.patientHomeLanguage && (
                    <>
                      <i className="fa fa-language me-2 text-primary"></i>
                      {patient.patientHomeLanguage} <br />
                    </>
                  )}
                </Col>

                <Col xs={12} md={8}>
                  {patient?.patientMedicalAid ? (
                    <>
                      <i className="fa fa-notes-medical me-2 text-primary"></i>
                      {patient.patientMedicalAid} <br />
                      <i className="fa fa-notes-medical me-2 text-white"></i>
                      {patient.patientMedicalAidPlan} |{" "}
                      {patient.patientMedicalAidOption} <br />
                      <i className="fa fa-notes-medical me-2 text-white"></i>
                      {patient.patientMedicalAidNumber} (
                      {patient.patientMedicalAidDependantCode}) <br />
                      <strong
                        className={getStatusClass(
                          patient.patientMedicalAidStatus
                        )}
                      >
                        <i className="fa-solid fa-thermometer me-2 text-primary"></i>
                        {patient.patientMedicalAidStatus} (
                        {patient.patientMedicalAidJoinDate})
                      </strong>
                      <br />
                      <small className="text-muted">
                        Last checked:{" "}
                        {patient?.patientMedicalAidLastCheck?.trim()
                          ? formatDate(patient.patientMedicalAidLastCheck)
                          : "N/A"}
                      </small>
                    </>
                  ) : (
                    <p className="text-primary mb-0">
                      <i className="fa fa-notes-medical me-2 text-primary"></i>
                      Private Patient
                    </p>
                  )}
                </Col>
              </Row>
            </>
          )}
        </Card.Body>
      </Card>
    );
  };

  // Left rail actions (FULL HEIGHT)
  const LeftRail = () => (
    <Container
      className="bg-white shadow-sm g-0 p-3 rounded-0 d-flex flex-column ms-3"
      style={{ position: "sticky", top: 0 }}
    >
      <div className="text-center mb-3">
        <Button
          variant={activeSection === "info" ? "primary" : "outline-primary"}
          className="w-100 text-start mb-2 rounded-0"
          onClick={() => setActiveSection("info")}
        >
          Patient Info
        </Button>

        {/* Edit Patient — simple button that opens the modal via the hook */}
        <div className="d-grid mb-2">
          <Button
            variant="primary"
            className="w-100 text-start rounded-0"
            onClick={() => openEditPatient(patient?._id)}
          >
            <i className="fa-solid fa-user-pen me-2" />
            Edit Patient
          </Button>
        </div>

        {patient?.patientMedicalAid && (
          <div className="d-grid mb-2">
            <Button
              variant={
                activeSection === "eligibility" ? "primary" : "outline-primary"
              }
              className="w-100 text-start rounded-0"
              onClick={() => setActiveSection("eligibility")}
            >
              <i className="fa-solid fa-stethoscope me-2" />
              Check Patient
            </Button>
          </div>
        )}

        <div className="d-grid">
          <Button
            variant={activeSection === "cases" ? "primary" : "outline-primary"}
            className="w-100 text-start rounded-0"
            onClick={() => {
              setActiveSection("cases");
              if (isMobile) setShowCasesPicker(true);
            }}
          >
            <i className="fa-solid fa-briefcase-medical me-2" />
            Patient Cases
          </Button>
        </div>
      </div>
    </Container>
  );

  const EligibilityPane = () => (
    <Card className="shadow-sm">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="mb-0 text-primary">Eligibility</h6>
          {patient?.patientMedicalAid ? (
            <CheckEligibility
              patient={patient}
              account={account}
              className="w-auto"
              onEligibility={(data) => setEligibilityData(data)}
            />
          ) : null}
        </div>

        {!patient?.patientMedicalAid ? (
          <Alert variant="warning" className="mb-0">
            Patient is not linked to a medical aid.
          </Alert>
        ) : eligibilityData ? (
          <pre className="mb-0 small">
            {JSON.stringify(eligibilityData, null, 2)}
          </pre>
        ) : (
          <div className="text-muted">No eligibility check performed yet.</div>
        )}
      </Card.Body>
    </Card>
  );

  const CasesPane = () => (
    <Card className="border-0">
      <Card.Body>
        <CaseManagerPanel
          patientId={patient?._id}
          existingReferrers={existingReferrers}
          onUseIcds={(sel) => {
            // wire to invoice selection later if needed
            console.log("Use ICDs from case:", sel);
          }}
          currentUserId={(() => {
            try {
              const token = sessionStorage.getItem("user");
              if (!token) return "";
              const decoded = jwtDecode(token);
              return decoded?._id || "";
            } catch {
              return "";
            }
          })()}
        />
      </Card.Body>
    </Card>
  );

  const renderRightPane = () => {
    if (activeSection === "info") return <PatientInfoCard />;
    if (activeSection === "eligibility") return <EligibilityPane />;
    if (activeSection === "cases") return <CasesPane />;
    return null;
  };

  return (
    <div className="ms-1" style={{ marginTop: "75px" }}>
      <Container fluid>
        <Row className="g-0">
          {/* LEFT rail always full height & sticky */}
          <Col md={2} className="pt-0 g-0 d-none d-md-block">
            <LeftRail />
          </Col>

          {/* On mobile, no static rail; right pane drives content */}
          <Col md={10} xs={12}>
            <Container className="pt-2 bg-transparent pb-3 mb-3 ms-2 rounded">
              {renderRightPane()}
            </Container>
          </Col>
        </Row>
      </Container>

      <hr className="mb-0" />

      {/* Sessions area (unchanged; role-gated) */}
      <Row className="ms-1 me-2">
        {userRole && (
          <Session
            // Top section of the mobile slide-out (Patient actions)
            extraMobileMenu={[
              {
                key: "patient-info",
                label: "Patient Info",
                onClick: () => setActiveSection("info"),
              },
              ...(patient?.patientMedicalAid
                ? [
                    {
                      key: "check-patient",
                      label: "Check Patient",
                      onClick: () => setActiveSection("eligibility"),
                    },
                  ]
                : []),
              {
                key: "cases",
                label: "Patient Cases",
                onClick: () => {
                  setActiveSection("cases");
                  setShowCasesPicker(true);
                },
              },
            ]}
            // Let Session tell the parent that a mobile action was taken (so you can scroll to top, etc)
            onMobileNavigate={(where) => {
              if (where === "patient") {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
              // 'session' is also passed when a Session tab is chosen; no action needed here.
            }}
          />
        )}
      </Row>

      {/* ⬇️ Mount the Edit Patient modal host (ONLY if using the hook) */}
      <EditPatientModalHost />
    </div>
  );
}
